import { ArrowRight, Check } from "lucide-react";

import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { SectionHeading } from "@/components/ui/SectionHeading";
import {
  ABOUT_COMMITMENTS,
  ABOUT_DIRECTION,
  ABOUT_EXPERIENCE,
  ABOUT_FINAL,
  ABOUT_HERO,
  ABOUT_OVERVIEW,
  ABOUT_PARTNERSHIP,
} from "@/data/about";
import { getIcon } from "@/lib/icons";

export default function About() {
  return (
    <>
      <SEO
        title="About MedCore Research"
        description={ABOUT_HERO.description}
        path="/about"
      />
      <PageHero
        eyebrow={ABOUT_HERO.eyebrow}
        title={ABOUT_HERO.title}
        description={ABOUT_HERO.description}
      />

      <Section data-testid="about-overview-section">
        <Container className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {ABOUT_OVERVIEW.map((paragraph, index) => (
            <Reveal key={paragraph.slice(0, 40)} delay={index * 0.08}>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                {paragraph}
              </p>
            </Reveal>
          ))}
        </Container>
      </Section>

      <Section muted data-testid="about-experience-section">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            <Reveal>
              <div className="lg:sticky lg:top-28">
                <p className="text-sm font-semibold uppercase tracking-wider text-accent">
                  Our Experience
                </p>
                <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
                  {ABOUT_EXPERIENCE.title}
                </h2>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                  {ABOUT_EXPERIENCE.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="rounded-3xl border border-border bg-background p-6 shadow-sm sm:p-8">
                <h3 className="text-xl font-semibold">Our experience includes</h3>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {ABOUT_EXPERIENCE.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3"
                    >
                      <Check
                        size={17}
                        strokeWidth={3}
                        className="mt-0.5 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <span className="text-sm leading-relaxed text-foreground/85">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section data-testid="about-partnership-section">
        <Container>
          <SectionHeading
            eyebrow="Our Partnership Model"
            title={ABOUT_PARTNERSHIP.title}
            description={ABOUT_PARTNERSHIP.introduction}
          />
          <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-2">
            {ABOUT_PARTNERSHIP.audiences.map((audience) => {
              const Icon = getIcon(audience.icon);
              return (
                <article
                  key={audience.title}
                  className="rounded-2xl border border-border bg-card p-7 shadow-sm sm:p-8"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon size={23} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-2xl font-semibold">{audience.title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">
                    {audience.description}
                  </p>
                </article>
              );
            })}
          </div>
          <p className="mx-auto mt-8 max-w-4xl rounded-2xl border-l-4 border-primary bg-primary/5 px-6 py-5 text-center text-base font-medium leading-relaxed text-foreground">
            {ABOUT_PARTNERSHIP.closing}
          </p>
        </Container>
      </Section>

      <Section muted data-testid="about-mission-vision-section">
        <Container className="grid gap-6 lg:grid-cols-2">
          {ABOUT_DIRECTION.map((item, index) => (
            <Reveal key={item.eyebrow} delay={index * 0.08}>
              <article className="h-full rounded-3xl border border-border bg-background p-7 shadow-sm sm:p-9">
                <p className="text-sm font-semibold uppercase tracking-wider text-accent">
                  {item.eyebrow}
                </p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
                  {item.title}
                </h2>
                <p className="mt-5 leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </article>
            </Reveal>
          ))}
        </Container>
      </Section>

      <Section data-testid="about-commitment-section">
        <Container>
          <SectionHeading eyebrow="Our Commitment" title="How We Support Every Study" />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
            {ABOUT_COMMITMENTS.map((commitment) => {
              const Icon = getIcon(commitment.icon);
              return (
                <article
                  key={commitment.title}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <Icon size={21} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold">{commitment.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {commitment.description}
                  </p>
                </article>
              );
            })}
          </div>
        </Container>
      </Section>

      <section className="bg-brand-emerald py-16 text-white sm:py-20" data-testid="about-final-cta">
        <Container className="text-center">
          <h2 className="text-balance text-3xl font-semibold sm:text-4xl">
            {ABOUT_FINAL.title}
          </h2>
          <div className="mx-auto mt-5 max-w-3xl space-y-3 text-base leading-relaxed text-white/85">
            {ABOUT_FINAL.paragraphs.map((paragraph) => (
              <p key={paragraph.slice(0, 40)}>{paragraph}</p>
            ))}
          </div>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button as="link" to="/contact?inquiry=practice#contact-form" size="lg">
              Become a MedCore Partner Site
              <ArrowRight size={18} aria-hidden="true" />
            </Button>
            <Button
              as="link"
              to="/contact?inquiry=study#contact-form"
              size="lg"
              variant="outline"
              className="border-white/40 bg-white/10 text-white hover:bg-white/20"
            >
              Submit a Study Opportunity
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}
